import { useState, useEffect, useMemo, useRef } from 'react'
import { useSocialActions } from 'src/store/social'
import type { Speed } from '../../types'
import { getAnimationDuration } from '../../utils/getAnimationDuration'

type Props = Readonly<{
  betId: string
  heightAnimation: number
  widthAnimation: number
  children: React.ReactNode
  speed?: Speed
  /**
   * Position in the batch that arrived together. Bets land in bursts — a round
   * closes and twenty reach the store in the same tick — and every one of them
   * used to start its float at the same instant from the same edge, so they
   * drew on top of each other and read as one smeared pill. The index spaces
   * their STARTS; the random horizontal offset below only ever spread them by
   * up to `widthAnimation`, which is narrower than the pill itself on a phone.
   */
  index?: number
}>

/** How many start slots the stagger cycles through before it wraps. */
const STAGGER_SLOTS = 10
/** Gap between two consecutive starts. */
const STAGGER_MS = 120

export function AnimatedSocialBet({
  speed,
  betId,
  children,
  widthAnimation,
  heightAnimation,
  index = 0
}: Props) {
  const actions = useSocialActions()
  const [isVisible, setIsVisible] = useState(true)

  // Memoize random values so they don't change on re-render
  const randomLeft = useMemo(() => Math.floor(Math.random() * widthAnimation), [widthAnimation])
  const duration = useMemo(() => getAnimationDuration(speed), [speed])
  // Captured at MOUNT. The list shifts as finished bets are dropped from the
  // store, so reading `index` later would re-time a float that is already
  // running and make it jump.
  const delayMs = useRef((index % STAGGER_SLOTS) * STAGGER_MS).current

  useEffect(() => {
    // Its own float, plus the wait for its turn. The old fixed 3000 ms cut a
    // `slow` bet (4 s) off a second before it finished, and with a stagger it
    // would have dropped the late ones sooner still.
    const timeoutId = setTimeout(
      () => {
        setIsVisible(false)
        actions.betProcessed(betId)
      },
      duration * 1000 + delayMs
    )

    return () => {
      clearTimeout(timeoutId)
    }
  }, [actions, betId, delayMs, duration])

  if (!isVisible) {
    return null
  }

  return (
    <div
      className="social-bet-stream__animated-inner"
      style={{
        marginLeft: randomLeft,
        // `both`, not `forwards`: the keyframes now open at opacity 0, so a bet
        // waiting for its slot is INVISIBLE rather than parked on the edge on
        // top of the one before it.
        animation: `socialBetFloat ${duration}s ease-out ${delayMs}ms both`,
        // CSS custom property for dynamic height
        ['--float-height' as string]: `-${heightAnimation}px`
      }}
    >
      {children}
    </div>
  )
}
